/**
 * Arena scoring & final ranking (spec §11 "Result screens").
 *
 * The Score formula is deliberately transparent — the result screen shows it broken
 * out line by line, so this module exposes the per-line breakdown, not just a total.
 * The backend recomputes the same total when it validates a submitted result, so the
 * formula here is the single canonical statement of the rules.
 *
 *   Score = (26 − survivalPlace) × 10   // survival: 1st = 250, 25th = 10
 *         + kills × 25                   // frags
 *         + goldBanked ÷ 10              // banked gold (rewards risk management)
 *         + (survived ? 100 : 0)         // bonus for lasting to the end
 */

export interface HeroResult {
  heroId: string;
  /** Finishing place, 1 = winner … 25 = first out. */
  survivalPlace: number;
  kills: number;
  goldBanked: number;
  /** Lasted to the end of the match (last standing, or alive at time expiry). */
  survived: boolean;
  /** Milliseconds alive — finer than place, used only as a tiebreaker. */
  timeAliveMs: number;
  /** Join order into the match (0 = entered first) — the last tiebreaker. */
  entryOrder: number;
}

export interface ScoreBreakdown {
  survival: number;   // (26 − place) × 10
  frags: number;      // kills × 25
  gold: number;       // floor(goldBanked / 10)
  survivalBonus: number; // 100 or 0
  total: number;
}

/** Compute the Score with every line kept separate for the result screen. */
export function scoreOf(r: Pick<HeroResult, 'survivalPlace' | 'kills' | 'goldBanked' | 'survived'>): ScoreBreakdown {
  const survival = (26 - r.survivalPlace) * 10;
  const frags = r.kills * 25;
  const gold = Math.floor(r.goldBanked / 10);
  const survivalBonus = r.survived ? 100 : 0;
  return { survival, frags, gold, survivalBonus, total: survival + frags + gold + survivalBonus };
}

/**
 * Order two results best-first: Score, then the spec's tiebreakers in order —
 * 1) more frags, 2) survived longer, 3) more gold banked, 4) entered earlier.
 * Returns <0 if `a` ranks above `b`.
 */
export function compareResults(a: HeroResult, b: HeroResult): number {
  const sa = scoreOf(a).total, sb = scoreOf(b).total;
  if (sa !== sb) return sb - sa;                 // higher score first
  if (a.kills !== b.kills) return b.kills - a.kills;             // 1) more frags
  if (a.timeAliveMs !== b.timeAliveMs) return b.timeAliveMs - a.timeAliveMs; // 2) longer
  if (a.goldBanked !== b.goldBanked) return b.goldBanked - a.goldBanked;     // 3) more gold
  return a.entryOrder - b.entryOrder;            // 4) entered earlier
}

export interface RankedResult extends HeroResult {
  score: ScoreBreakdown;
  /** Final leaderboard rank after Score + tiebreakers (1 = best). */
  rank: number;
}

/** Rank a whole match's results into the final combined leaderboard. */
export function rankMatch(results: HeroResult[]): RankedResult[] {
  return [...results]
    .sort(compareResults)
    .map((r, i) => ({ ...r, score: scoreOf(r), rank: i + 1 }));
}

/**
 * The one-line coaching hint under the result (spec: "explain, don't just display").
 * Picks the biggest lever this player left on the table.
 */
export function improvementHint(r: HeroResult): string {
  // Died with a real streak unbanked → banking is the lesson.
  if (!r.survived && r.goldBanked < r.kills * 25) return 'Bank gold more often — you lost half your streak when you died.';
  if (!r.survived && r.survivalPlace > 5) return 'Last until the end of the match — survival weighs more than frags.';
  if (!r.survived) return 'You led on frags but died — survival weighs more. Play the final safer.';
  if (r.kills === 0) return 'Survival is good, but frags and banked gold win — take a few fights.';
  return 'Solid all-round game. Push for one more bank before the match closes in.';
}

/**
 * What a survivor is worth when the clock runs out.
 *
 * A battle royale normally ends with one fighter standing, but this one has a
 * 210s timer and usually ends with a dozen alive. The old ordering ranked those
 * survivors by kills → gold → HP, which is a different formula from the Score the
 * result screen prints; and whoever came out on top was labelled "VICTORY" purely
 * for still breathing. Owner: at timer expiry rank the survivors by the Score
 * formula and give 1st place to the highest — and only that hero, place 1, is a
 * victory.
 *
 * This is the Score formula minus its survival term, which is what we are ranking
 * FOR and so cannot be an input. The survival bonus is +100 for every survivor
 * alike, so it drops out too. Remaining HP and time alive are tiebreakers only —
 * they are not Score, they just stop a tie being a coin flip.
 */
export interface SurvivorStanding {
  kills: number;
  goldBanked: number;
  /** Remaining HP as a fraction of max (tiebreaker only). */
  hpPct: number;
  timeAliveMs: number;
  entryOrder: number;
}

/** The place-independent part of Score: frags + banked gold. Higher is better. */
export function survivorMerit(s: Pick<SurvivorStanding, 'kills' | 'goldBanked'>): number {
  return s.kills * 25 + Math.floor(s.goldBanked / 10);
}

/** Order two survivors best-first. Returns <0 if `a` outranks `b`. */
export function compareSurvivors(a: SurvivorStanding, b: SurvivorStanding): number {
  const m = survivorMerit(b) - survivorMerit(a);
  if (m) return m;
  if (a.hpPct !== b.hpPct) return b.hpPct - a.hpPct;
  if (a.timeAliveMs !== b.timeAliveMs) return b.timeAliveMs - a.timeAliveMs;
  return a.entryOrder - b.entryOrder;
}
